import React, { useEffect, useState } from "react"
import { Link } from "react-router-dom"
import GatedMetric from '../../components/Common/GatedMetric'
import PlatformIcon from '../../components/Common/PlatformIcon'
import { usePlatforms } from '../../contexts/PlatformsContext'
import { generateSlug } from '../../utils/seoUtils'
import { useAnalytics, ANALYTICS_EVENTS } from '../../analytics'
const PublisherDataTable = ({ inputdata, gamesData, cType, onLockClick, searchType }) => {
  const { getActivePlatforms } = usePlatforms();
  const { trackEvent } = useAnalytics();
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" })
  const [sortedData, setSortedData] = useState([])
  const LOCK_PLACEHOLDER = "%UPGRADE_KEYQ%"

  const formatNumber = num => {
    if (num >= 1e6) return (num / 1e6).toFixed(1) + " M"
    if (num >= 1e3) return (num / 1e3).toFixed(1) + " K"
    return num?.toString() || "0"
  }

  const formatDate = dateString => {
    const date = new Date(dateString)
    const day = String(date.getDate()).padStart(2, "0")
    const month = String(date.getMonth() + 1).padStart(2, "0")
    const year = String(date.getFullYear()).slice(-2)
    return `${day}-${month}-${year}`
  }

  const calculateRating = (ratingData) => {
    if (!ratingData || !ratingData.rating_index || !ratingData.total_rating) return 0

    const totalRatings = ratingData.total_rating
    const ratings = ratingData.rating_index

    const weightedSum =
      (ratings["1"] || 0) * 1 +
      (ratings["2"] || 0) * 2 +
      (ratings["3"] || 0) * 3 +
      (ratings["4"] || 0) * 4 +
      (ratings["5"] || 0) * 5

    return (weightedSum / totalRatings).toFixed(1)
  }

  useEffect(() => {
    setSortedData(gamesData || [])
  }, [gamesData])

  const handleSort = (key) => {
    const direction = sortConfig.key === key && sortConfig.direction === "asc" ? "desc" : "asc"

    const sorted = [...(gamesData || [])].sort((a, b) => {
      let aValue, bValue

      switch (key) {
        case "name":
          aValue = a.name || ""
          bValue = b.name || ""
          break
        case "category":
          aValue = a.category || inputdata || ""
          bValue = b.category || inputdata || ""
          break
        case "publisher":
          aValue = a.publisher || a.developer || ""
          bValue = b.publisher || b.developer || ""
          break
        case "subcategory":
          aValue = a.genre?.join(", ") || ""
          bValue = b.genre?.join(", ") || ""
          break
        case "price":
          aValue = a.price?.amount || 0
          bValue = b.price?.amount || 0
          break
        case "rating":
          aValue = parseFloat(calculateRating(a.rating)) || 0
          bValue = parseFloat(calculateRating(b.rating)) || 0
          break
        case "downloads":
          aValue = a.downloads || 0
          bValue = b.downloads || 0
          break
        case "revenue":
          aValue = a.revenue || 0
          bValue = b.revenue || 0
          break
        default:
          return 0
      }

      // If either side is a string, compare as strings (safe coercion).
      if (typeof aValue === "string" || typeof bValue === "string") {
        const aStr = aValue == null ? '' : String(aValue)
        const bStr = bValue == null ? '' : String(bValue)
        return direction === "asc" ? aStr.localeCompare(bStr) : bStr.localeCompare(aStr)
      }

      // Otherwise compare numerically.
      return direction === "asc" ? aValue - bValue : bValue - aValue
    })

    setSortedData(sorted)
    setSortConfig({ key, direction })
    trackEvent(ANALYTICS_EVENTS.FEATURE.PUBLISHER_EXPLORER_SORT_CHANGED, { sort_key: key, direction })
  }

  const SortIcon = ({ column }) => {
    if (sortConfig.key !== column) {
      return <span className="sort-icon">↕️</span>
    }
    return sortConfig.direction === "asc" ?
      <span className="sort-icon active">↑</span> :
      <span className="sort-icon active">↓</span>
  }

  return (
    <div className="responsive-table-container">
      {/* Desktop Table */}
      <div className="desktop-table">
        <table className="modern-table">
          <thead>
            <tr>
              <th>#</th>
              <th onClick={() => handleSort("name")} className="sortable" aria-sort={sortConfig.key === 'name' ? (sortConfig.direction === 'asc' ? 'ascending' : 'descending') : 'none'}>
                Game <SortIcon column="name" />
              </th>
              <th onClick={() => handleSort("publisher")} className="sortable" aria-sort={sortConfig.key === 'publisher' ? (sortConfig.direction === 'asc' ? 'ascending' : 'descending') : 'none'}>
                Publisher <SortIcon column="publisher" />
              </th>
              <th onClick={() => handleSort("category")} className="sortable" aria-sort={sortConfig.key === 'category' ? (sortConfig.direction === 'asc' ? 'ascending' : 'descending') : 'none'}>
                Category <SortIcon column="category" />
              </th>
              <th onClick={() => handleSort("subcategory")} className="sortable" aria-sort={sortConfig.key === 'subcategory' ? (sortConfig.direction === 'asc' ? 'ascending' : 'descending') : 'none'}>
                Sub Category <SortIcon column="subcategory" />
              </th>
              <th onClick={() => handleSort("price")} className="sortable" aria-sort={sortConfig.key === 'price' ? (sortConfig.direction === 'asc' ? 'ascending' : 'descending') : 'none'}>
                Price <SortIcon column="price" />
              </th>
              <th onClick={() => handleSort("rating")} className="sortable" aria-sort={sortConfig.key === 'rating' ? (sortConfig.direction === 'asc' ? 'ascending' : 'descending') : 'none'}>
                Ratings <SortIcon column="rating" />
              </th>
              <th onClick={() => handleSort("downloads")} className="sortable" aria-sort={sortConfig.key === 'downloads' ? (sortConfig.direction === 'asc' ? 'ascending' : 'descending') : 'none'}>
                Downloads <SortIcon column="downloads" />
              </th>
              <th onClick={() => handleSort("revenue")} className="sortable" aria-sort={sortConfig.key === 'revenue' ? (sortConfig.direction === 'asc' ? 'ascending' : 'descending') : 'none'}>
                Revenue <SortIcon column="revenue" />
              </th>
              <th>Release On</th>
            </tr>
          </thead>
          <tbody>
            {sortedData.map((game, index) => (
              <tr key={game.item_id || index}>
                <td>{index + 1}</td>
                <td>
                  <div className="game-cell">
                    <Link
                      to={`/xr/app/${generateSlug(game.name || '')}?id=${game.item_id}`}
                      className="game-link"
                      state={{ app_id: game.item_id, data: game, source_surface: "publisher_explorer_results" }}
                      onClick={() => trackEvent(ANALYTICS_EVENTS.FEATURE.PUBLISHER_DASHBOARD_APP_SELECTED, { app_id: game.item_id, app_name: game.name })}
                    >
                      <img
                        src={game.image_url || "https://via.placeholder.com/40"}
                        alt={game.name}
                        className="game-image"
                      />
                      <div className="game-info">
                        <div className="game-name">{game.name}</div>
                        <div className="game-platform-icons">
                          {(() => {
                            try {
                              const platformRaw = game.platform || game.platforms || game.game?.platform || ''
                              const platform = String(platformRaw || '').toLowerCase()
                              const platformUrl = game.platform_url || game.platformUrl || game.game?.platform_url || ''

                              const openPlatformLink = (e, pGame = game) => {
                                if (e) { e.stopPropagation(); e.preventDefault(); }
                                const name = pGame.name || ''
                                if (platformUrl && platformUrl !== '#') {
                                  window.open(platformUrl, '_blank', 'noopener')
                                  return
                                }
                                
                                // Dynamic URL building using platform data
                                const buildPlatformUrl = () => {
                                  const activePlatforms = getActivePlatforms();
                                  const currentPlatform = activePlatforms.find(p => p.id === platform);
                                  
                                  if (currentPlatform && currentPlatform.url_template) {
                                    // Use platform-specific URL template from API
                                    const template = currentPlatform.url_template;
                                    const replacements = {
                                      '{name}': encodeURIComponent(pGame.name || ''),
                                      '{id}': pGame.item_id || pGame.id || '',
                                      '{slug}': pGame.slug || pGame.meta_slug || pGame.experience_slug || '',
                                      '{app_id}': pGame.app_id || '',
                                      '{platform_id}': platform,
                                    };
                                    
                                    let url = template;
                                    Object.entries(replacements).forEach(([placeholder, value]) => {
                                      url = url.replace(new RegExp(placeholder.replace(/[{}]/g, '\\$&'), 'g'), value);
                                    });
                                    
                                    // If template has placeholders that weren't replaced, try fallback
                                    if (url.includes('{') || url.includes('}')) {
                                      return buildFallbackUrl();
                                    }
                                    
                                    return url;
                                  }
                                  
                                  // Fallback to existing hardcoded logic
                                  return buildFallbackUrl();
                                };
                                
                                const buildFallbackUrl = () => {
                                  try {
                                    // Meta / Oculus
                                    const metaItemId = pGame.meta_item_id || pGame.metaItemId || pGame.item_id || pGame.itemId || pGame.game_id || pGame.game?.item_id
                                    if (!platformUrl && (platform.includes('meta') || platform.includes('oculus')) && metaItemId) {
                                      const rawSlug = pGame.slug || pGame.game?.slug || pGame.name || name || ''
                                      const slug = String(rawSlug).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')
                                      const metaUrl = `https://www.meta.com/experiences/${slug}/${metaItemId}`
                                      return metaUrl
                                    }
                                    // Steam
                                    const steamAppId = pGame.steam_appid || pGame.steamAppId || pGame.steam?.appid
                                    if (!platformUrl && platform.includes('steam') && steamAppId) {
                                      const steamUrl = `https://store.steampowered.com/app/${steamAppId}/`
                                      return steamUrl
                                    }
                                    // Apple Vision
                                    const appleId = pGame.apple_item_id || pGame.appleItemId || pGame.apple_id || pGame.game?.apple_item_id
                                    if (!platformUrl && platform.includes('apple') && appleId) {
                                      const appleUrl = `https://apps.apple.com/app/id${appleId}`
                                      return appleUrl
                                    }
                                  } catch (err) {
                                    console.warn('Error building fallback URL', err)
                                  }

                                  // Final fallback: search
                                  const domain = platform.includes('meta') ? 'oculus.com' : platform.includes('steam') ? 'store.steampowered.com' : platform.includes('apple') ? 'apps.apple.com' : ''
                                  const q = domain ? `${name} site:${domain}` : name
                                  return `https://www.google.com/search?q=${encodeURIComponent(q)}`
                                };
                                
                                try {
                                  const url = buildPlatformUrl();
                                  window.open(url, '_blank', 'noopener');
                                } catch (err) {
                                  console.warn('Error opening platform link', err)
                                }
                              }

                              const icons = []
                              // Dynamic platform detection using active platforms
                              const activePlatforms = getActivePlatforms();
                              const isKnownPlatform = activePlatforms.some(p => 
                                platform.includes(p.id.toLowerCase()) || 
                                p.aliases?.some(alias => platform.includes(alias.toLowerCase()))
                              );
                              
                              if (isKnownPlatform) {
                                icons.push(
                                  <a key={platform} href={platformUrl || '#'} target="_blank" rel="noopener noreferrer" title={platform} onClick={(e) => openPlatformLink(e)}>
                                    <PlatformIcon platform={platform} size={16} />
                                  </a>
                                )
                              }

                              // If no platform string but store links exist, try to show generic icons using dynamic platform detection
                              if (!icons.length) {
                                const url = game.platform_url || game.platformUrl || game.meta_url || ''

                                // Check if URL matches any active platform
                                const matchedPlatform = activePlatforms.find(p => {
                                  if (url.includes(p.id.toLowerCase())) return true;
                                  if (p.aliases?.some(alias => url.includes(alias.toLowerCase()))) return true;
                                  return false;
                                });

                                if (matchedPlatform) {
                                  icons.push(
                                    <a key={matchedPlatform.id} href={url} target="_blank" rel="noopener noreferrer" title={matchedPlatform.display_name}>
                                      <PlatformIcon platform={matchedPlatform.id} size={16} />
                                    </a>
                                  );
                                }
                              }

                              return icons.length ? icons : null
                            } catch (err) {
                              console.warn('Platform icon render error', err)
                              return null
                            }
                          })()}
                        </div>
                      </div>
                    </Link>
                  </div>
                </td>
                <td>
                  {(game.publisher || game.developer) ? (
                    <Link
                      to={`/publisher-apps/${game.item_id}`}
                      className="publisher-button"
                      aria-label={`Open ${game.publisher || game.developer} dashboard`}
                      onClick={() => trackEvent(ANALYTICS_EVENTS.FEATURE.PUBLISHER_NAME_CLICKED, { publisher_name: game.publisher || game.developer })}
                    >
                      {game.publisher || game.developer}
                    </Link>
                  ) : (
                    <span className="publisher-button publisher-disabled" aria-disabled="true">N/A</span>
                  )}
                </td>
                <td><span className="category-tag">{game.category || inputdata}</span></td>
                <td>
                  <div className="subcategory-tags">
                    {game.genre?.slice(0, 3).map((genre, idx) => (
                      <span key={idx} className="subcategory-tag">{genre}</span>
                    ))}
                  </div>
                </td>
                <td className="price-cell">$ {game.price?.amount?.toFixed(2) || "0.00"}</td>
                <td>
                  <div className="rating-cell">
                    <span className="rating-value">{calculateRating(game.rating)}</span>
                    <span className="rating-star">★</span>
                  </div>
                </td>
                <td className="downloads-cell">{
                  (() => {
                    const raw = game.downloads ?? game?.stats?.downloads
                    const isPlaceholder = raw === LOCK_PLACEHOLDER
                    const numeric = typeof raw === 'number' ? raw : (typeof raw === 'string' && raw !== LOCK_PLACEHOLDER ? Number(String(raw).replace(/[,\s]/g, '')) : NaN)
                    const display = isFinite(numeric) ? formatNumber(numeric) : (raw || 0)

                    return isPlaceholder ? (
                      <span onClick={() => onLockClick && onLockClick()} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: onLockClick ? 'pointer' : 'default' }} role="button" title="Upgrade to view downloads">
                        <span style={{ filter: 'blur(4px)', WebkitFilter: 'blur(4px)' }}>{display}</span>
                        <i className="fas fa-lock" aria-hidden="true" style={{ fontSize: 12, color: '#fff' }} />
                      </span>
                    ) : (
                      display
                    )
                  })()
                }</td>
                <td className="revenue-cell">{
                  (() => {
                    const raw = game.revenue ?? game?.stats?.revenue
                    const isPlaceholder = raw === LOCK_PLACEHOLDER
                    const numeric = typeof raw === 'number' ? raw : (typeof raw === 'string' && raw !== LOCK_PLACEHOLDER ? Number(String(raw).replace(/[,\s]/g, '')) : NaN)
                    const display = isFinite(numeric) ? formatNumber(Math.floor(numeric)) : (raw || 0)

                    return isPlaceholder ? (
                      <span onClick={() => onLockClick && onLockClick()} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: onLockClick ? 'pointer' : 'default' }} role="button" title="Upgrade to view revenue">
                        <span style={{ filter: 'blur(4px)', WebkitFilter: 'blur(4px)' }}>$ {display}</span>
                        <i className="fas fa-lock" aria-hidden="true" style={{ fontSize: 12, color: '#fff' }} />
                      </span>
                    ) : (
                      <>$ {display}</>
                    )
                  })()
                }</td>
                <td className="date-cell">{formatDate(game.created_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile Cards */}
      <div className="mobile-cards">
        {sortedData.map((game, index) => (
          <div key={game.item_id || index} className="mobile-card">
            <div className="mobile-card-header">
              <span className="mobile-index">#{index + 1}</span>
              <span className="mobile-rating">
                {calculateRating(game.rating)} ★
              </span>
            </div>
            <div className="mobile-card-content">
              <Link to={`/xr/app/${generateSlug(game.name || '')}?id=${game.item_id}`} className="mobile-game-link">
                <img
                  src={game.image_url || "https://via.placeholder.com/60"}
                  alt={game.name}
                  className="mobile-game-image"
                />
                <div className="mobile-game-info">
                  <div className="mobile-game-name">{game.name}</div>
                  <div className="game-platform-icons">
                    {(() => {
                      try {
                        const platformRaw = game.platform || game.platforms || game.game?.platform || ''
                        const platform = String(platformRaw || '').toLowerCase()
                        const platformUrl = game.platform_url || game.platformUrl || game.game?.platform_url || ''

                        const openPlatformLink = (e, pGame = game) => {
                          if (e) { e.stopPropagation(); e.preventDefault(); }
                          const name = pGame.name || ''
                          if (platformUrl && platformUrl !== '#') {
                            window.open(platformUrl, '_blank', 'noopener')
                            return
                          }
                          
                          // Use the same dynamic URL building logic as desktop version
                          const buildPlatformUrl = () => {
                            const activePlatforms = getActivePlatforms();
                            const currentPlatform = activePlatforms.find(p => p.id === platform);
                            
                            if (currentPlatform && currentPlatform.url_template) {
                              const template = currentPlatform.url_template;
                              const replacements = {
                                '{name}': encodeURIComponent(pGame.name || ''),
                                '{id}': pGame.item_id || pGame.id || '',
                                '{slug}': pGame.slug || pGame.meta_slug || pGame.experience_slug || '',
                                '{app_id}': pGame.app_id || '',
                                '{platform_id}': platform,
                              };
                              
                              let url = template;
                              Object.entries(replacements).forEach(([placeholder, value]) => {
                                url = url.replace(new RegExp(placeholder.replace(/[{}]/g, '\\$&'), 'g'), value);
                              });
                              
                              if (url.includes('{') || url.includes('}')) {
                                return buildFallbackUrl();
                              }
                              
                              return url;
                            }
                            
                            return buildFallbackUrl();
                          };
                          
                          const buildFallbackUrl = () => {
                            try {
                              const metaItemId = pGame.meta_item_id || pGame.metaItemId || pGame.item_id || pGame.itemId || pGame.game_id || pGame.game?.item_id
                              if (!platformUrl && (platform.includes('meta') || platform.includes('oculus')) && metaItemId) {
                                const rawSlug = pGame.slug || pGame.game?.slug || pGame.name || name || ''
                                const slug = String(rawSlug).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')
                                const metaUrl = `https://www.meta.com/experiences/${slug}/${metaItemId}`
                                return metaUrl
                              }
                              const steamAppId = pGame.steam_appid || pGame.steamAppId || pGame.steam?.appid
                              if (!platformUrl && platform.includes('steam') && steamAppId) {
                                const steamUrl = `https://store.steampowered.com/app/${steamAppId}/`
                                return steamUrl
                              }
                              const appleId = pGame.apple_item_id || pGame.appleItemId || pGame.apple_id || pGame.game?.apple_item_id
                              if (!platformUrl && platform.includes('apple') && appleId) {
                                const appleUrl = `https://apps.apple.com/app/id${appleId}`
                                return appleUrl
                              }
                            } catch (err) {
                              console.warn('Error building fallback URL', err)
                            }

                            const domain = platform.includes('meta') ? 'oculus.com' : platform.includes('steam') ? 'store.steampowered.com' : platform.includes('apple') ? 'apps.apple.com' : ''
                            const q = domain ? `${name} site:${domain}` : name
                            return `https://www.google.com/search?q=${encodeURIComponent(q)}`
                          };
                          
                          try {
                            const url = buildPlatformUrl();
                            window.open(url, '_blank', 'noopener');
                          } catch (err) {
                            console.warn('Error opening platform link', err)
                          }
                        }

                        const icons = []
                        // Dynamic platform detection using active platforms
                        const activePlatforms = getActivePlatforms();
                        const isKnownPlatform = activePlatforms.some(p => 
                          platform.includes(p.id.toLowerCase()) || 
                          p.aliases?.some(alias => platform.includes(alias.toLowerCase()))
                        );
                        
                        if (isKnownPlatform) {
                          icons.push(
                            <a key={platform} href={platformUrl || '#'} target="_blank" rel="noopener noreferrer" title={platform} onClick={(e) => openPlatformLink(e)}>
                              <PlatformIcon platform={platform} size={16} />
                            </a>
                          )
                        }

                        // If no platform string but store links exist, try to show generic icons using dynamic platform detection
                        if (!icons.length) {
                          const url = game.platform_url || game.platformUrl || game.meta_url || ''

                          // Check if URL matches any active platform
                          const matchedPlatform = activePlatforms.find(p => {
                            if (url.includes(p.id.toLowerCase())) return true;
                            if (p.aliases?.some(alias => url.includes(alias.toLowerCase()))) return true;
                            return false;
                          });
                          
                          if (matchedPlatform) {
                            icons.push(
                              <a key={matchedPlatform.id} href={url} target="_blank" rel="noopener noreferrer" title={matchedPlatform.display_name}>
                                <PlatformIcon platform={matchedPlatform.id} size={16} />
                              </a>
                            );
                          }
                        }

                        return icons.length ? icons : null
                      } catch (err) {
                        console.warn('Platform icon render error', err)
                        return null
                      }
                    })()}
                  </div>
                  <div className="mobile-category">{game.category || inputdata}</div>
                </div>
              </Link>
            </div>
            <div className="mobile-card-stats">
              <div className="mobile-stat">
                <span className="mobile-stat-label">Price</span>
                <span className="mobile-stat-value">$ {game.price?.amount?.toFixed(2) || "0.00"}</span>
              </div>
              <div className="mobile-stat">
                <span className="mobile-stat-label">Downloads</span>
                <span className="mobile-stat-value">{(() => {
                  const raw = game.downloads ?? game?.stats?.downloads
                  const isPlaceholder = raw === LOCK_PLACEHOLDER
                  const numeric = typeof raw === 'number' ? raw : (typeof raw === 'string' && raw !== LOCK_PLACEHOLDER ? Number(String(raw).replace(/[,\s]/g, '')) : NaN)
                  const display = isFinite(numeric) ? formatNumber(numeric) : (raw || 0)

                  return isPlaceholder ? (
                    <span onClick={() => onLockClick && onLockClick()} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: onLockClick ? 'pointer' : 'default' }} role="button" title="Upgrade to view downloads">
                      <span style={{ filter: 'blur(4px)', WebkitFilter: 'blur(4px)' }}>{display}</span>
                      <i className="fas fa-lock" aria-hidden="true" style={{ fontSize: 12, color: '#fff' }} />
                    </span>
                  ) : (
                    display
                  )
                })()}</span>
              </div>
              <div className="mobile-stat">
                <span className="mobile-stat-label">Revenue</span>
                <span className="mobile-stat-value">{(() => {
                  const raw = game.revenue ?? game?.stats?.revenue
                  const isPlaceholder = raw === LOCK_PLACEHOLDER
                  const numeric = typeof raw === 'number' ? raw : (typeof raw === 'string' && raw !== LOCK_PLACEHOLDER ? Number(String(raw).replace(/[,\s]/g, '')) : NaN)
                  const display = isFinite(numeric) ? formatNumber(Math.floor(numeric)) : (raw || 0)

                  return isPlaceholder ? (
                    <span onClick={() => onLockClick && onLockClick()} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: onLockClick ? 'pointer' : 'default' }} role="button" title="Upgrade to view revenue">
                      <span style={{ filter: 'blur(4px)', WebkitFilter: 'blur(4px)', opacity: 0.6, userSelect: 'none', pointerEvents: 'none' }}>$ {display}</span>
                      <i className="fas fa-lock" aria-hidden="true" style={{ fontSize: 12, color: '#fff', cursor: 'pointer' }} />
                    </span>
                  ) : (
                    <>$ {display}</>
                  )
                })()}</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      <style>{`
        .responsive-table-container {
              width: 100%;
              margin-top: 20px;
              /* allow horizontal scrolling on small viewports and on mac trackpads */
              overflow-x: auto;
              -webkit-overflow-scrolling: touch;
            }

        /* Desktop Table Styles */
        .desktop-table {
          display: block;
        }

        .modern-table {
          width: 100%;
          min-width: 1100px; /* ensure table can overflow horizontally and be scrolled */
          border-collapse: collapse;
          border-radius: 12px;
          overflow: hidden;
          border: 1px solid #353748;
        }

        .modern-table thead {
          background: #2a2b37;
          
        }

        .modern-table th {
          padding: 16px 12px;
          text-align: left;
          font-weight: 500;
          font-size: 14px;
          color: #b0b0b5;
          border-bottom: 1px solid #353748;
          white-space: nowrap;
          
        }

        .modern-table th.sortable {
          cursor: pointer;
          user-select: none;
          transition: background-color 0.2s;
        }

        .modern-table th.sortable:hover {
          background: #3a3b47;
        }

        .sort-icon {
          margin-left: 4px;
          font-size: 12px;
          opacity: 0.5;
        }

        .sort-icon.active {
          opacity: 1;
          color: #F43F5E;
        }

        .modern-table td {
          padding: 12px;
          border-bottom: 1px solid #353748;
          font-size: 14px;
          color: #fff;
        }

        .modern-table tbody tr:hover {
          background: #2a2b37;
        }

        .game-cell {
          display: flex;
          align-items: center;
          gap: 12px;
          min-width: 200px;
        }

        .game-link {
          display: flex;
          align-items: center;
          gap: 12px;
          text-decoration: none;
          color: inherit;
        }

        .game-image {
          width: 40px;
          height: 40px;
          border-radius: 8px;
          object-fit: cover;
        }

        .game-info {
          flex: 1;
        }

        .game-name {
          font-weight: 500;
          color: #fff;
          margin-bottom: 2px;
        }

        .game-publisher {
          font-size: 12px;
          color: #b0b0b5;
        }

        .game-publisher a {
          color: #F43F5E;
          text-decoration: none;
        }

        .publisher-button {
          display: inline-block;
          background: #2a2b37;
          border: 1px solid #3a3b47;
          color: #F43F5E;
          padding: 6px 10px;
          border-radius: 8px;
          font-weight: 600;
          text-decoration: none;
          white-space: nowrap;
        }

        .publisher-button-inline {
          display: inline-block;
          background: transparent;
          color: #F43F5E;
          padding: 0;
          border: none;
          text-decoration: underline;
          font-weight: 600;
        }

        /* Platform icons shown under game name */
        .game-platform-icons {
          display: flex;
          gap: 8px;
          align-items: center;
          margin-top: 6px;
        }
        .game-platform-icons a { display: inline-flex; align-items: center; }
        .game-platform-icons img { width: 18px; height: 18px; object-fit: contain; }
        .game-platform-icons .platform-steam { color: #b0b0b5; font-size: 16px; }

        .publisher-disabled {
          color: #9aa0a6;
          cursor: default;
          opacity: 0.9;
          text-decoration: none;
          font-weight: 600;
        }

        .category-tag {
          background: #2a2b37;
          color: #b0b0b5;
          padding: 4px 8px;
          border-radius: 4px;
          font-size: 12px;
        }

        .subcategory-tags {
          display: flex;
          gap: 4px;
          flex-wrap: wrap;
        }

        .subcategory-tag {
          background: #353748;
          color: #b0b0b5;
          padding: 2px 6px;
          border-radius: 3px;
          font-size: 11px;
        }

        .price-cell {
          font-weight: 500;
          color: #4ade80;
        }

        .rating-cell {
          display: flex;
          align-items: center;
          gap: 4px;
        }

        .rating-value {
          font-weight: 500;
        }

        .rating-star {
          color: #fbbf24;
        }

        .downloads-cell,
        .revenue-cell {
          font-weight: 500;
        }

        .date-cell {
          color: #b0b0b5;
          font-size: 12px;
        }

        /* Mobile Cards */
        .mobile-cards {
          display: none;
        }

        .mobile-card {
          background: #21222D;
          border: 1px solid #353748;
          border-radius: 12px;
          padding: 16px;
          margin-bottom: 12px;
        }

        .mobile-card-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 12px;
        }

        .mobile-index {
          color: #b0b0b5;
          font-size: 12px;
        }

        .mobile-rating {
          color: #fbbf24;
          font-weight: 500;
        }

        .mobile-card-content {
          margin-bottom: 16px;
        }

        .mobile-game-link {
          display: flex;
          gap: 12px;
          text-decoration: none;
          color: inherit;
        }

        .mobile-game-image {
          width: 60px;
          height: 60px;
          border-radius: 8px;
          object-fit: cover;
        }

        .mobile-game-info {
          flex: 1;
        }

        .mobile-game-name {
          font-weight: 500;
          color: #fff;
          margin-bottom: 4px;
        }

        .mobile-game-publisher {
          font-size: 12px;
          color: #b0b0b5;
          margin-bottom: 4px;
        }

        .mobile-game-publisher a {
          color: #F43F5E;
          text-decoration: none;
        }

        .mobile-category {
          background: #2a2b37;
          color: #b0b0b5;
          padding: 2px 6px;
          border-radius: 4px;
          font-size: 11px;
          display: inline-block;
        }

        .mobile-card-stats {
          display: grid;
          grid-template-columns: 1fr 1fr 1fr;
          gap: 12px;
        }

        .mobile-stat {
          text-align: center;
        }

        .mobile-stat-label {
          display: block;
          font-size: 11px;
          color: #b0b0b5;
          margin-bottom: 2px;
        }

        .mobile-stat-value {
          display: block;
          font-weight: 500;
          color: #fff;
          font-size: 12px;
        }

        /* Responsive Breakpoints */
        @media (max-width: 1024px) {
          .modern-table th,
          .modern-table td {
            padding: 8px 6px;
            font-size: 13px;
          }
          
          .game-cell {
            min-width: 150px;
          }
          
          .game-image {
            width: 35px;
            height: 35px;
          }
        }

        @media (max-width: 768px) {
          .desktop-table {
            display: none;
          }
          
          .mobile-cards {
            display: block;
          }
        }
      `}</style>
    </div>
  )
}

export default PublisherDataTable
